import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Badge,
  Spinner,
  Form,
  ListGroup
} from "react-bootstrap";
import axios from "axios";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import RoomActionMenu from "./RoomActionMenu";

export default function RoomLayoutDashboard() {
  const userData = useSelector((state) => state.authentication.userData);
  const API_BASE = `${process.env.REACT_APP_COLLEGE_ERP_API}`;

  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
const [selectedReservation, setSelectedReservation] = useState(null);

  // Filters
  const [selectedFloor, setSelectedFloor] = useState("All");
  const [selectedFeature, setSelectedFeature] = useState("");
  const [showOnlyVacant, setShowOnlyVacant] = useState(false);

  const today = dayjs().startOf("day");

  /* ---------------- FETCH ---------------- */
  const fetchData = async () => {
    if (!userData?.branch || !userData?.token) return;

    setLoading(true);
    try {
      const [roomRes, reservationRes] = await Promise.all([
        axios.get(`${API_BASE}rooms?branch=${userData.branch}`, {
          headers: { Authorization: `Bearer ${userData.token}` }
        }),
        axios.get(`${API_BASE}reservations?branch=${userData.branch}`, {
          headers: { Authorization: `Bearer ${userData.token}` }
        })
      ]);

      setRooms(roomRes.data || []);
      setReservations(reservationRes.data || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [userData]);

  /* ---------------- STATUS ---------------- */
 const getStatus = (room) => {
  const booking = reservations.find((r) => {
    const checkIn = dayjs(r.checkInDate).startOf("day");
    const checkOut = dayjs(r.checkOutDate).startOf("day");

    const hasRoom = r.rooms?.some(
      (rm) => rm.roomNumber === room.roomNumber
    );

    return (
      hasRoom &&
      ["reserved", "confirmed", "checked-in"].includes(r.bookingStatus) &&
      today.isSameOrAfter(checkIn, "day") &&
      today.isBefore(checkOut, "day")
    );
  });

  return booking ? booking.bookingStatus : "vacant";
};

  const getColor = (status) => {
  switch (status) {
    case "reserved": return "#f0ad4e";
    case "confirmed": return "#5bc0de";
    case "checked-in": return "#d9534f";
    case "maintenance": return "#777";
    default: return "#5cb85c"; // vacant
  }
};

  /* ---------------- FILTERS ---------------- */

  const floors = useMemo(() => {
    const f = rooms.map((r) => r.floor || "Ground Floor");
    return ["All", ...new Set(f)];
  }, [rooms]);

  const filteredRooms = useMemo(() => {
    return rooms.filter((room) => {
      const status = getStatus(room);

      if (selectedFloor !== "All" && room.floor !== selectedFloor)
        return false;

      if (selectedFeature && room.type !== selectedFeature)
        return false;

      if (showOnlyVacant && status !== "vacant")
        return false;

      return true;
    });
  }, [rooms, selectedFloor, selectedFeature, showOnlyVacant, reservations]);

  /* ---------------- STATUS COUNT ---------------- */

  const statusCount = useMemo(() => {
  const count = {
    vacant: 0,
    reserved: 0,
    confirmed: 0,
    "checked-in": 0
  };

  rooms.forEach((room) => {
    const s = getStatus(room);
    count[s] = (count[s] || 0) + 1;
  });

  return count;
}, [rooms, reservations]);

  /* ---------------- UI ---------------- */
 



  return (
    <Row>
            <Card className="mt-3 p-3">
  <h6>Room Details</h6>

  {!selectedReservation ? (
    <div style={{ color: "#888" }}>
      Select an occupied room to view details
    </div>
  ) : (
    <Row>
     <Col md={4}>
  <div><strong>Guest Name:</strong> {selectedReservation.guestName}</div>
  <div><strong>Company:</strong> {selectedReservation.company || "-"}</div>

  <div>
    <strong>Arrival:</strong>{" "}
    {dayjs(selectedReservation.checkInDate).format("DD MMM YYYY")}
  </div>

  <div>
    <strong>Departure:</strong>{" "}
    {dayjs(selectedReservation.checkOutDate).format("DD MMM YYYY")}
  </div>

  {/* ✅ NEW */}
  <div>
    <strong>Rooms:</strong>{" "}
    {selectedReservation.rooms?.map(r => r.roomNumber).join(", ") || "-"}
  </div>

  <div>
    <strong>Units:</strong>{" "}
    {selectedReservation.rooms?.length || 0}
  </div>
</Col>

    <Col md={4}>
  <div><strong>Guest Type:</strong> {selectedReservation.guestType || "-"}</div>
  <div><strong>Payment:</strong> {selectedReservation.paymentMode || "-"}</div>

  <div>
    <strong>Total Amount:</strong>{" "}
    ₹{selectedReservation.totalAmount || 0}
  </div>
</Col>

      <Col md={4}>
        <div><strong>Total Revenue:</strong> ₹{selectedReservation.totalAmount || 0}</div>
        <div><strong>Room Revenue:</strong> ₹{selectedReservation.roomRevenue || 0}</div>
        <div><strong>Deposits:</strong> ₹{selectedReservation.deposit || 0}</div>
        <div><strong>Credit Limit:</strong> ₹{selectedReservation.creditLimit || 0}</div>
        <div><strong>Group ID:</strong> {selectedReservation.groupId || "-"}</div>
      </Col>
    </Row>
  )}
</Card>
    {/* LEFT - ROOM GRID */}
<Col md={9}>
  <Card className="p-3">
    <h5>
      Room Layout - {today.format("DD MMM YYYY")}
    </h5>

    {loading ? (
      <Spinner />
    ) : (
      <Row className="g-3 mt-2">
        {filteredRooms.map((room) => {
          const status = getStatus(room);

          // Find the specific reservation 'r' for this room to pass into the Menu component
          const currentReservation = reservations.find((r) => {
            const checkIn = dayjs(r.checkInDate).startOf("day");
            const checkOut = dayjs(r.checkOutDate).startOf("day");
            const hasRoom = r.rooms?.some((rm) => rm.roomNumber === room.roomNumber);

            return (
              hasRoom &&
              ["reserved", "confirmed", "checked-in"].includes(r.bookingStatus) &&
              today.isSameOrAfter(checkIn, "day") &&
              today.isBefore(checkOut, "day")
            );
          });

          return (
            <Col xs={6} sm={4} md={3} lg={2} key={room.roomNumber}>
              <div
                style={{
                  backgroundColor: getColor(status),
                  color: "#fff",
                  padding: "12px",
                  borderRadius: "10px",
                  textAlign: "center",
                  cursor: "pointer",
                  border: selectedRoom?.roomNumber === room.roomNumber
                    ? "3px solid #000"
                    : "none",
                }}
                onClick={() => {
                  setSelectedRoom(room);
                  setSelectedReservation(currentReservation || null);
                }}
              >
                {/* Room Identifier */}
                <div className="fw-bold mb-2">{room.roomNumber}</div>
                
                {/* Action Menu Component Dropdown */}
                <div onClick={(e) => e.stopPropagation()}>
                  <RoomActionMenu
                    room={room}
                    userData={userData}
                    API_BASE={API_BASE}
                    refreshRooms={fetchData}
                    reservation={currentReservation}
                  />
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    )}

    {/* LEGEND */}
    <div className="mt-3">
      <Badge bg="success">Vacant</Badge>{" "}
      <Badge bg="warning">Reserved</Badge>{" "}
      <Badge bg="info">Confirmed</Badge>{" "}
      <Badge bg="danger">Occupied</Badge>{" "}
    </div>
  </Card>
</Col>

      {/* RIGHT PANEL */}
      <Col md={3}>
        <Card className="p-2 mb-2">
          <h6>Floors</h6>
          <ListGroup>
            {floors.map((f) => (
              <ListGroup.Item
                key={f}
                active={selectedFloor === f}
                onClick={() => setSelectedFloor(f)}
                style={{ cursor: "pointer" }}
              >
                {f}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card>

        <Card className="p-2 mb-2">
          <h6>Room Features</h6>
          <Form.Check
            type="radio"
            label="1 BHK"
            onChange={() => setSelectedFeature("1BHK")}
          />
          <Form.Check
            type="radio"
            label="2 BHK"
            onChange={() => setSelectedFeature("2BHK")}
          />
        </Card>

        <Card className="p-2 mb-2">
          <h6>Status Summary</h6>
          <div>Vacant: {statusCount.vacant}</div>
<div>Reserved: {statusCount.reserved}</div>
<div>Occupied: {statusCount["checked-in"]}</div>
        </Card>

        <Card className="p-2">
          <Form.Check
            label="Show Only Vacant"
            onChange={(e) => setShowOnlyVacant(e.target.checked)}
          />
        </Card>
      </Col>
  
    </Row>

  );
}